import React from "react";
import { Box } from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * PopupMarkdown
 *
 * Renders backend-supplied Markdown for any popup type.
 * All Markdown-specific styling lives here so every popup template
 * (info, action, notification, etc.) gets identical rendering rules.
 *
 * Props:
 * - content: string | null | undefined  — raw Markdown from the backend
 * - sx: object — optional style overrides merged onto the wrapper Box
 */
const PopupMarkdown = ({ content, sx = {} }) => {
    // Handle empty/null Markdown safely — render nothing instead of crashing
    // or showing an empty box with weird spacing.
    if (!content || typeof content !== "string" || content.trim() === "") {
        return null;
    }

    return (
        <Box
            sx={{
                color: "rgba(255, 255, 255, 0.85)",
                fontSize: "0.9rem",
                lineHeight: 1.6,
                wordBreak: "break-word",

                "& > *:first-of-type": { marginTop: 0 },
                "& > *:last-child": { marginBottom: 0 },

                "& p": {
                    margin: "0 0 12px 0",
                },

                "& h1, & h2, & h3, & h4, & h5, & h6": {
                    color: "#ffffff",
                    fontWeight: 700,
                    lineHeight: 1.3,
                    margin: "16px 0 8px 0",
                },
                "& h1": { fontSize: "1.4rem" },
                "& h2": { fontSize: "1.2rem" },
                "& h3": { fontSize: "1.05rem" },
                "& h4": { fontSize: "0.95rem" },

                "& ul, & ol": {
                    margin: "0 0 12px 0",
                    paddingLeft: "22px",
                },
                "& li": {
                    marginBottom: "4px",
                },

                "& a": {
                    color: "#8ab4ff",
                    textDecoration: "underline",
                    "&:hover": {
                        color: "#a9c6ff",
                    },
                },

                "& strong": {
                    fontWeight: 700,
                    color: "#ffffff",
                },
                "& em": {
                    fontStyle: "italic",
                },

                "& blockquote": {
                    margin: "0 0 12px 0",
                    padding: "8px 14px",
                    borderLeft: "3px solid rgba(255, 255, 255, 0.4)",
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderRadius: "4px",
                    color: "rgba(255, 255, 255, 0.75)",
                },

                "& code": {
                    fontFamily:
                        'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                    fontSize: "0.85em",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "4px",
                    padding: "1px 5px",
                },
                "& pre": {
                    margin: "0 0 12px 0",
                    padding: "10px 12px",
                    backgroundColor: "rgba(0, 0, 0, 0.3)",
                    borderRadius: "6px",
                    overflowX: "auto",
                },
                "& pre code": {
                    backgroundColor: "transparent",
                    padding: 0,
                },

                "& hr": {
                    border: "none",
                    borderTop: "1px solid rgba(255, 255, 255, 0.15)",
                    margin: "16px 0",
                },

                "& table": {
                    borderCollapse: "collapse",
                    width: "100%",
                    marginBottom: "12px",
                },
                "& th, & td": {
                    border: "1px solid rgba(255, 255, 255, 0.15)",
                    padding: "6px 10px",
                    textAlign: "left",
                },

                ...sx,
            }}
        >
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    a: ({ href, children, ...props }) => {
                        const safeHref = href || "";
                        const isExternal = /^https?:\/\//i.test(safeHref);
                        return (
                            <a
                                {...props}
                                href={safeHref}
                                target={isExternal ? "_blank" : undefined}
                                rel={
                                    isExternal
                                        ? "noopener noreferrer"
                                        : undefined
                                }
                            >
                                {children}
                            </a>
                        );
                    },
                }}
            >
                {content}
            </ReactMarkdown>    
        </Box>
    );
};

export default PopupMarkdown;
